import { router } from "@inertiajs/react";

// Inertia's router is a module singleton that exists as soon as its chunk
// loads, but it is only initialised (page and resolveComponent assigned) from
// a useEffect inside <App>, after the first page component has been imported
// and rendered. Anything that calls router.visit / router.reload in that
// window (a cold-start deep link or push tap) is not a clean no-op:
// visit() first calls saveScrollPositions(), whose replaceState writes
// `{...undefined, scrollRegions}` into router.page, so the request goes out
// as normal. When the Inertia response lands, setPage() runs
// `this.resolveComponent(...)` and throws "this.resolveComponent is not a
// function" as an unhandled rejection (Honeybadger fault 135064604, issue
// #7003). The page also loses its own component, version and props.

// True once router.init() has run and the first page is current.
export const isInertiaReady = () => Boolean(router.page?.component);

// True when this document is an Inertia page that will initialise the router
// (server-rendered ERB pages have no #app root and never do).
export const inertiaWillBoot = () =>
  typeof document !== "undefined" && Boolean(document.querySelector("div#app"));

// Run `callback` now if the router is initialised, otherwise on the first
// "navigate" event, which Inertia fires once init has shown the first page.
// Returns a function that cancels a pending callback.
export const whenInertiaReady = (callback) => {
  if (isInertiaReady()) {
    callback();
    return () => {};
  }
  const stop = router.on("navigate", () => {
    stop();
    callback();
  });
  return stop;
};
